// src/api/invoices.api.ts
import { API } from '.'
import { API_BASE_URL } from '@/config'
import Cookies from 'js-cookie'

import type {
  Invoice,
  InvoiceStatusUi,
  InvoiceScopeUi,
  InvoiceTypeUi,
  ServiceBillingInvoice,
  InvoiceApprovalStatusUi,
  InvoiceHistoryEntry,
} from '@/components/invoices/invoice.types'
import type {
  InvoiceApi,
  InvoiceListItemApi,
  InvoiceStatusApi,
  GetInvoicesResponseApi,
  GetInvoicesParams,
  CreateDraftInvoiceRequest,
  UpdateInvoiceItemsRequest,
  RegenerateInvoicesRequest,
  RegenerateInvoicesResponse,
  UpdateDraftInvoiceRequest,
  InvoiceHistoryItemApi,
  GetInvoiceHistoryResponseApi,
  NextInvoiceNumberResponseApi,
} from '@/api/dto/invoice.dto'

// -----------------------------
// Mapping helpers (API -> UI)
// -----------------------------
function eurToCents(eur: number) {
  return Math.round((eur ?? 0) * 100)
}

function dateToIsoStart(d: string) {
  return `${d}T00:00:00`
}
function dateToIsoEnd(d: string) {
  return `${d}T23:59:59`
}

function mapStatus(s: InvoiceStatusApi): InvoiceStatusUi {
  return s
}

function firstNonZeroCustomerId(items?: { customerId: number }[]) {
  const hit = (items ?? []).find(it => Number(it.customerId) > 0)?.customerId
  const n = Number(hit ?? 0)
  return n > 0 ? n : 0 // 0 means “fallback to bankName”
}

function normalizeInvoiceType(t: unknown): InvoiceTypeUi {
  const v = String(t ?? '').toUpperCase()
  if (v === 'MANUAL' || v === 'AUTOMATIC') return v
  return 'UNKNOWN'
}

function normalizeInvoiceScope(s: unknown): InvoiceScopeUi {
  const v = String(s ?? '').toUpperCase()
  if (v === 'ACTIVITY' || v === 'ORDER' || v === 'MEDIATION') return v
  return 'UNKNOWN'
}

function normalizeApprovalStatus(v: unknown): InvoiceApprovalStatusUi {
  const s = String(v ?? '').toUpperCase()
  if (s === 'PENDING_APPROVAL') return 'PENDING_APPROVAL'
  if (s === 'APPROVED') return 'APPROVED'
  if (s === 'REJECTED') return 'REJECTED'
  return 'NA'
}

function mapInvoiceListItemToUi(inv: InvoiceListItemApi): Invoice {
  return {
    id: String(inv.id),
    bankId: inv.bankId,
    bankName: inv.bankName,

    type: normalizeInvoiceType(inv.type),
    scope: normalizeInvoiceScope(inv.scope),

    customerId: firstNonZeroCustomerId(inv.items),
    periodStart: inv.periodStart,
    periodEnd: inv.periodEnd,
    periodFrom: dateToIsoStart(inv.periodStart),
    periodTo: dateToIsoEnd(inv.periodEnd),
    invoiceDate: inv.createdAt,
    invoiceNumber: inv.invoiceNumber,
    status: mapStatus(inv.status),
    totalCents: eurToCents(inv.totalGross),

    comment: '',
    lines: [],
    items: (inv.items ?? []).map(it => ({
      id: String(it.id),
      entityId: it.entityId,
      entityType: it.entityType,
      serviceType: it.serviceType,
      customerId: it.customerId,
      serviceRecipientName: (it as any).serviceRecipientName ?? null,
      billingVariant: (it as any).billingVariant ?? null,
      description: it.description ?? null,
      quantity: it.quantity,
      grossRate: it.grossRate,
      netAmount: it.netAmount,
      taxAmount: it.taxAmount,
      grossAmount: it.grossAmount,
      activityDate: (it as any).activityDate ?? null,
      activityNotes: (it as any).activityNotes ?? null,
      orderId: (it as any).orderId ?? null,
      periodStart: it.periodStart ?? null,
      periodEnd: it.periodEnd ?? null,
      position: (it as any).position ?? undefined,
      scope: (it as any).scope ?? null,
    })),

    sentAt: inv.sentAt ?? null,
    finalizedAt: inv.finalizedAt ?? null,
    createdAt: inv.createdAt,
    approvalStatus: normalizeApprovalStatus((inv as any).approvalStatus),
    canceledAt: inv.canceledAt,
    canceledInvoiceId: inv.canceledInvoiceId != null ? String(inv.canceledInvoiceId) : null,
    cancellationInvoiceId:
      inv.cancellationInvoiceId != null ? String(inv.cancellationInvoiceId) : null,
  }
}

function mapInvoiceListItemToServiceBillingUi(inv: InvoiceListItemApi): ServiceBillingInvoice {
  return {
    id: String(inv.id),
    bankId: inv.bankId,
    bankName: inv.bankName,
    rejectionReason: (inv as any).rejectionReason ?? null,

    type: normalizeInvoiceType(inv.type),
    scope: normalizeInvoiceScope(inv.scope),

    customerId: firstNonZeroCustomerId(inv.items),

    periodStart: inv.periodStart,
    periodEnd: inv.periodEnd,
    periodFrom: dateToIsoStart(inv.periodStart),
    periodTo: dateToIsoEnd(inv.periodEnd),
    invoiceDate: inv.createdAt,
    invoiceNumber: inv.invoiceNumber,
    status: mapStatus(inv.status),
    totalCents: eurToCents(inv.totalGross),

    totalGross: inv.totalGross,
    totalNet: inv.totalNet,
    taxAmount: inv.taxAmount,
    vatPercentage: inv.vatPercentage ?? 0,
    // approvalStatus: inv.approvalStatus,
    approvalStatus: normalizeApprovalStatus(inv.approvalStatus),
    consultantName: inv.userName,

    comment: '',
    lines: [],
    items: (inv.items ?? []).map(it => ({
      id: String(it.id),
      entityId: it.entityId,
      entityType: it.entityType,
      serviceType: it.serviceType,
      customerId: it.customerId,
      serviceRecipientName: (it as any).serviceRecipientName ?? null,
      billingVariant: (it as any).billingVariant ?? null,
      description: it.description ?? null,
      quantity: it.quantity,
      grossRate: it.grossRate,
      netAmount: it.netAmount,
      taxAmount: it.taxAmount,
      grossAmount: it.grossAmount,
      activityDate: (it as any).activityDate ?? null,
      activityNotes: (it as any).activityNotes ?? null,
      orderId: (it as any).orderId ?? null,
      periodStart: it.periodStart ?? null,
      periodEnd: it.periodEnd ?? null,
      position: (it as any).position ?? undefined,
      scope: (it as any).scope ?? null,
    })),
    summary: inv.summary ?? null,

    sentAt: inv.sentAt ?? null,
    finalizedAt: inv.finalizedAt ?? null,
    createdAt: inv.createdAt,
    canceledAt: inv.canceledAt ?? null,
    canceledInvoiceId: inv.canceledInvoiceId != null ? String(inv.canceledInvoiceId) : null,
    cancellationInvoiceId:
      inv.cancellationInvoiceId != null ? String(inv.cancellationInvoiceId) : null,
  }
}

function serviceGroupLabel(serviceType: string): string {
  switch (serviceType) {
    case 'TOJ_CUSTOMER':
      return 'ToJ mit Kunde'
    case 'TOJ':
      return 'ToJ'
    case 'CONSULTING':
      return 'Beratung'
    case 'TRAINING':
      return 'Schulung'
    case 'PILOT':
      return 'Pilot'
    default:
      return serviceType
  }
}

function mapInvoiceDetailsToUi(inv: InvoiceApi): Invoice {
  const customerId = (inv.items ?? []).find(it => Number(it.customerId) > 0)?.customerId ?? 0
  return {
    id: String(inv.id),
    bankId: inv.bankId,
    contactDetails: inv.contactDetails
      ? {
          contactName: inv.contactDetails.contactName ?? null,
          contactEmail: inv.contactDetails.contactEmail ?? null,
          contactPhone: inv.contactDetails.contactPhone ?? null,
        }
      : null,
    customerId,
    bankName: inv.bankName,
    type: normalizeInvoiceType(inv.type),
    scope: normalizeInvoiceScope(inv.scope),
    periodStart: inv.periodStart,
    periodEnd: inv.periodEnd,
    periodFrom: dateToIsoStart(inv.periodStart),
    periodTo: dateToIsoEnd(inv.periodEnd),
    invoiceDate: inv.sentAt ?? inv.finalizedAt ?? inv.createdAt,
    invoiceNumber: inv.invoiceNumber,
    status: mapStatus(inv.status),
    totalCents: eurToCents(inv.totalGross),
    rejectionReason: inv.rejectionReason ?? null,
    comment: inv.comments ?? '',
    cancellationReason: inv.cancellationReason ?? null,

    lines: (inv.items ?? []).map(it => ({
      id: String(it.id),
      entityId: it.entityId, // <-- this is what you send in itemsIds
      customerId: it.customerId,
      serviceType: it.serviceType,

      serviceGroup: serviceGroupLabel(it.serviceType),
      leistung: serviceGroupLabel(it.serviceType),
      menge: it.quantity,
      preisProEinheitCents: eurToCents(it.grossRate),
      billingVariant: (it as any).billingVariant ?? null,
    })),
    items: (inv.items ?? []).map(it => ({
      id: String(it.id),
      entityId: it.entityId,
      entityType: it.entityType,
      serviceType: it.serviceType,
      customerId: it.customerId,
      serviceRecipientName: (it as any).serviceRecipientName ?? null,
      billingVariant: (it as any).billingVariant ?? null,
      description: it.description ?? null,
      quantity: it.quantity,
      grossRate: it.grossRate,
      netAmount: it.netAmount,
      taxAmount: it.taxAmount,
      grossAmount: it.grossAmount,
      activityDate: (it as any).activityDate ?? null,
      activityNotes: (it as any).activityNotes ?? null,
      orderId: (it as any).orderId ?? null,
      periodStart: it.periodStart ?? null,
      periodEnd: it.periodEnd ?? null,
      position: (it as any).position ?? undefined,
      scope: (it as any).scope ?? null,
    })),

    sentAt: inv.sentAt ?? null,
    finalizedAt: inv.finalizedAt ?? null,
    createdAt: inv.createdAt,
    approvalStatus: normalizeApprovalStatus((inv as any).approvalStatus),
    canceledAt: inv.canceledAt ?? null,
    canceledInvoiceId: inv.canceledInvoiceId != null ? String(inv.canceledInvoiceId) : null,
    cancellationInvoiceId:
      inv.cancellationInvoiceId != null ? String(inv.cancellationInvoiceId) : null,
  }
}

// function mapInvoiceListItemToUi(inv: InvoiceListItemApi): ServiceBillingInvoice {
//   return {
//     id: String(inv.id),
//     bankId: inv.bankId,
//     bankName: inv.bankName,

//     type: normalizeInvoiceType(inv.type),
//     scope: normalizeInvoiceScope(inv.scope),

//     customerId: firstNonZeroCustomerId(inv.items),

//     periodFrom: dateToIsoStart(inv.periodStart),
//     periodTo: dateToIsoEnd(inv.periodEnd),
//     invoiceDate: inv.createdAt,
//     invoiceNumber: inv.invoiceNumber,
//     status: mapStatus(inv.status),
//     totalCents: eurToCents(inv.totalGross),

//     totalGross: inv.totalGross,
//     totalNet: inv.totalNet,
//     taxAmount: inv.taxAmount,
//     vatPercentage: inv.vatPercentage,
//     approvalStatus: inv.approvalStatus,
//     consultantName: inv.userName,

//     comment: '',
//     lines: [],

//     sentAt: inv.sentAt ?? null,
//     finalizedAt: inv.finalizedAt ?? null,
//     createdAt: inv.createdAt,

//     canceledAt: inv.canceledAt,
//     canceledInvoiceId: inv.canceledInvoiceId != null ? String(inv.canceledInvoiceId) : null,
//     cancellationInvoiceId:
//       inv.cancellationInvoiceId != null ? String(inv.cancellationInvoiceId) : null,
//   }
// }

// -----------------------------
// Public API (used by widget)
// -----------------------------

export async function getInvoices(params: GetInvoicesParams): Promise<{ items: Invoice[] }> {
  const size = params.size ?? 100 // or 50
  let page = params.page ?? 0

  const all: Invoice[] = []

  while (true) {
    const qs = new URLSearchParams()
    Object.entries({ ...params, page, size }).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return
      qs.set(k, String(v))
    })

    const res = await API<GetInvoicesResponseApi>(
      `/invoices?${qs.toString()}`,
      {},
      'Fehler beim Laden der Rechnungen',
      'INVOICES_FETCH_ERROR'
    )

    all.push(...(res.content ?? []).map(mapInvoiceListItemToUi))

    // stop condition (Spring Page has "last")
    if (res.last || (res.content?.length ?? 0) < size) break

    page += 1
  }

  return { items: all }
}

export async function getInvoiceById(id: string): Promise<Invoice> {
  const data = await API<InvoiceApi>(
    `/invoices/${Number(id)}`,
    {},
    'Fehler beim Laden der Rechnung',
    'INVOICE_FETCH_ERROR'
  )
  return mapInvoiceDetailsToUi(data)
}

export async function createDraftInvoice(
  body: CreateDraftInvoiceRequest
): Promise<{ id: string }> {
  const data = await API<InvoiceApi>(
    `/invoices`,
    { method: 'POST', body: JSON.stringify(body) },
    'Fehler beim Erstellen des Entwurfs',
    'INVOICE_CREATE_DRAFT_ERROR'
  )
  return { id: String(data.id) }
}

/**
 * Preview the invoice number the next ACTIVITY draft would receive for `date` (the draft's
 * `periodStart`, `YYYY-MM-DD`). Read-only on the backend: nothing is generated or reserved, so the
 * eventual draft number may still differ. Backend contract (SIB-944):
 * - 200 `{ invoiceNumber }`
 * - 404 when automatic invoice numbering is disabled in the owner's settings
 * - 400 when the owner's invoice settings are missing
 *
 * `ownerUserId` targets the ADMIN-only `/next-number/{userId}` route; omit it for the signed-in
 * user. The shared GET cache is bypassed so a reopened dialog always shows a fresh snapshot, and
 * the request is marked non-critical so a failing preview never takes the app offline.
 */
export async function previewNextInvoiceNumber(
  date: string,
  ownerUserId?: string
): Promise<string | null> {
  const base = ownerUserId
    ? `/invoices/next-number/${encodeURIComponent(ownerUserId)}`
    : '/invoices/next-number'
  const path = `${base}?date=${encodeURIComponent(date)}`

  // Bypass the shared GET cache: every call must reflect the current sequence. The preview is
  // decorative, so a 5xx here must not open the app-wide outage overlay either.
  const data = await API<NextInvoiceNumberResponseApi>(
    path,
    { headers: { 'x-disable-cache': '1' }, nonCritical: true },
    'Fehler beim Ermitteln der nächsten Rechnungsnummer',
    'INVOICE_NEXT_NUMBER_ERROR'
  )

  const number = typeof data?.invoiceNumber === 'string' ? data.invoiceNumber.trim() : ''
  return number || null
}

export async function updateInvoiceItems(
  id: string,
  body: UpdateInvoiceItemsRequest
): Promise<void> {
  await API(
    `/invoices/${Number(id)}/items`,
    { method: 'PUT', body: JSON.stringify(body) },
    'Fehler beim Aktualisieren der Rechnungspositionen',
    'INVOICE_UPDATE_ITEMS_ERROR'
  )
}

export async function finalizeInvoice(id: string): Promise<{ id: string }> {
  const data = await API<InvoiceApi>(
    `/invoices/${Number(id)}/finalize`,
    { method: 'POST' },
    'Fehler beim Finalisieren der Rechnung',
    'INVOICE_FINALIZE_ERROR'
  )

  return { id: String(data.id) }
}

export async function sendInvoiceById(id: string): Promise<{ id: string }> {
  const data = await API<InvoiceApi>(
    `/invoices/${Number(id)}/send`,
    { method: 'POST' },
    'Fehler beim Versenden der Rechnung',
    'INVOICE_SEND_ERROR'
  )

  return { id: String(data.id) }
}

export async function deleteDraftInvoice(id: string): Promise<void> {
  await API(
    `/invoices/${Number(id)}`,
    { method: 'DELETE' },
    'Fehler beim Löschen des Entwurfs',
    'INVOICE_DELETE_DRAFT_ERROR'
  )
}

export async function submitInvoice(id: string): Promise<{ id: string }> {
  const data = await API<InvoiceApi>(
    `/invoices/${Number(id)}/submit`,
    { method: 'POST' },
    'Fehler beim Einreichen der Rechnung',
    'INVOICE_SUBMIT_ERROR'
  )

  return { id: String(data.id) }
}

export async function downloadInvoice(
  id: string,
  type: 'pdf' | 'xml' | 'zip',
  filename?: string
): Promise<void> {
  const csrf =
    localStorage.getItem('CSRF_TOKEN') ||
    Cookies.get('CSRF_TOKEN') ||
    Cookies.get('XSRF-TOKEN') || // common name
    ''

  const accept =
    type === 'pdf' ? 'application/pdf' : type === 'xml' ? 'application/xml' : 'application/zip'

  const url = `${API_BASE_URL}/invoices/${Number(id)}/${type}`

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      // try the common Spring names (header name matters for some setups)
      'X-CSRF-TOKEN': csrf,
      Accept: accept,
    },
  })

  if (!res.ok) {
    // IMPORTANT: surface backend error text for debugging
    const text = await res.text().catch(() => '')
    throw new Error(`Download fehlgeschlagen (${res.status}): ${text || res.statusText}`)
  }

  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename ?? `invoice-${id}.${type}`
  document.body.appendChild(a)
  a.click()
  a.remove()

  URL.revokeObjectURL(objectUrl)
}

/**
 * Open an invoice PDF in a new browser tab (preview, not download).
 * Fetches the PDF as a blob so we can send credentials/CSRF, then opens an object URL.
 */
export async function openInvoicePdf(id: string): Promise<void> {
  const csrf =
    localStorage.getItem('CSRF_TOKEN') ||
    Cookies.get('CSRF_TOKEN') ||
    Cookies.get('XSRF-TOKEN') ||
    ''

  const url = `${API_BASE_URL}/invoices/${Number(id)}/pdf`

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-CSRF-TOKEN': csrf,
      Accept: 'application/pdf',
    },
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Vorschau fehlgeschlagen (${res.status}): ${text || res.statusText}`)
  }

  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  window.open(objectUrl, '_blank', 'noopener,noreferrer')

  // give the new tab time to load before releasing the object URL
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

export async function cancelInvoice(
  id: string,
  body: { cancellationReason: string; sendCancellationInvoice: boolean }
): Promise<{
  originalInvoice: { id: string; cancellationInvoiceId?: string | null }
  cancellationInvoice: { id: string; canceledInvoiceId?: string | null }
}> {
  const data = await API<{
    originalInvoice: { id: number; cancellationInvoiceId?: number | null }
    cancellationInvoice: { id: number; canceledInvoiceId?: number | null }
  }>(
    `/invoices/${Number(id)}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({
        cancellationReason: body.cancellationReason,
        sendCancellationInvoice: body.sendCancellationInvoice,
      }),
    },
    'Fehler beim Stornieren der Rechnung',
    'INVOICE_CANCEL_ERROR'
  )

  return {
    originalInvoice: {
      id: String(data.originalInvoice.id),
      cancellationInvoiceId:
        data.originalInvoice.cancellationInvoiceId != null
          ? String(data.originalInvoice.cancellationInvoiceId)
          : null,
    },
    cancellationInvoice: {
      id: String(data.cancellationInvoice.id),
      canceledInvoiceId:
        data.cancellationInvoice.canceledInvoiceId != null
          ? String(data.cancellationInvoice.canceledInvoiceId)
          : null,
    },
  }
}

//Regenerate invoices
export async function regenerateInvoices(
  body: RegenerateInvoicesRequest
): Promise<RegenerateInvoicesResponse | void> {
  // Endpoint: POST /invoices/batch/regenerate
  return await API(
    `/invoices/batch/regenerate`,
    { method: 'POST', body: JSON.stringify(body) },
    'Fehler beim Regenerieren der Rechnung',
    'INVOICE_REGENERATE_ERROR'
  )
}

export async function getServiceBillingInvoices(
  params: GetInvoicesParams
): Promise<{ items: ServiceBillingInvoice[] }> {
  const size = params.size ?? 100
  let page = params.page ?? 0

  const all: ServiceBillingInvoice[] = []

  while (true) {
    const qs = new URLSearchParams()
    Object.entries({ ...params, page, size }).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return
      qs.set(k, String(v))
    })

    const res = await API<GetInvoicesResponseApi>(
      `/invoices?${qs.toString()}`,
      {},
      'Fehler beim Laden der Rechnungen',
      'INVOICES_FETCH_ERROR'
    )

    all.push(...(res.content ?? []).map(mapInvoiceListItemToServiceBillingUi))

    if (res.last || (res.content?.length ?? 0) < size) break

    page += 1
  }

  return { items: all }
}
// Update draft invoice (e.g. after editing items, period, customer, etc.)

export async function updateDraftInvoice(
  invoiceId: string,
  payload: UpdateDraftInvoiceRequest
): Promise<Invoice> {
  const data = await API<InvoiceApi>(
    `/invoices/${Number(invoiceId)}/items`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
    'Fehler beim Aktualisieren des Rechnungsentwurfs',
    'INVOICE_UPDATE_DRAFT_ERROR'
  )

  return mapInvoiceDetailsToUi(data)
}

export async function rejectInvoice(
  id: string,
  body: { rejectionReason: string }
): Promise<{
  id: string
  status?: string
  approvalStatus?: string
  rejectionReason?: string | null
}> {
  const data = await API<{
    id: number
    status?: string
    approvalStatus?: string
    rejectionReason?: string | null
  }>(
    `/invoices/${Number(id)}/reject`,
    {
      method: 'POST',
      body: JSON.stringify({
        rejectionReason: body.rejectionReason,
      }),
    },
    'Fehler beim Ablehnen der Rechnung',
    'INVOICE_REJECT_ERROR'
  )

  return {
    id: String(data.id),
    status: data.status,
    approvalStatus: data.approvalStatus,
    rejectionReason: data.rejectionReason ?? null,
  }
}

function mapInvoiceHistoryItemToUi(it: InvoiceHistoryItemApi): InvoiceHistoryEntry {
  return {
    id: String(it.id),
    action: it.action,
    performedBy: it.performedBy ?? null,
    username: it.username ?? null,
    reason: it.reason ?? null,
    timestamp: it.ts,
  }
}

// Audit log for a single invoice (status changes, regenerations, cancellations).
// Returned newest-first regardless of backend page ordering.
export async function getInvoiceHistory(id: string): Promise<InvoiceHistoryEntry[]> {
  const size = 100
  let page = 0

  const all: InvoiceHistoryItemApi[] = []

  while (true) {
    const res = await API<GetInvoiceHistoryResponseApi>(
      `/invoices/${Number(id)}/history?page=${page}&size=${size}`,
      {},
      'Fehler beim Laden des Rechnungsverlaufs',
      'INVOICE_HISTORY_FETCH_ERROR'
    )

    all.push(...(res.content ?? []))

    if (res.last || (res.content?.length ?? 0) < size) break

    page += 1
  }

  return all
    .map(mapInvoiceHistoryItemToUi)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
}

export async function approveInvoice(id: string): Promise<{
  id: string
  invoiceNumber?: string
  status?: string
  approvalStatus?: string
}> {
  const data = await API<{
    id: number
    invoiceNumber?: string
    status?: string
    approvalStatus?: string
  }>(
    `/invoices/${Number(id)}/approve`,
    {
      method: 'POST',
    },
    'Fehler beim Freigeben der Rechnung',
    'INVOICE_APPROVE_ERROR'
  )

  return {
    id: String(data.id),
    invoiceNumber: data.invoiceNumber,
    status: data.status,
    approvalStatus: data.approvalStatus,
  }
}
